import get from 'lodash.get';
import keyBy from 'lodash.keyby';

// Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

// Global Types
import { AppState } from 'services/types';

// Global selectors
import {
    makeSelectQueryStringValue,
    makeSelectQueryStringNumberValue
} from 'workspaces/react-router-selectors';

// Local Types
import orderBy from 'lodash.orderby';
import { makeSelectHarvestSchemaEntityTypeId } from 'services/schemas/selectors';
import { selectEntities } from 'services/entities/selectors';
import { selectSprayEventTypeIsWeed } from 'services/sprayEventType/selectors';
import {
    selectFirstDate,
    selectLastDate
    // @ts-ignore
} from 'components/DatePagination/selectors';
import {
    SprayEvent,
    SprayEventsState,
    SprayEventsProps,
    SprayEventId,
    SprayEventFilterType,
    SprayEventSearchFilters
} from './types';
import { getSprayEventVarietiesArray } from './utils';
import {
    EQUIPMENT_FILTER_TYPE,
    PRODUCT_FILTER_TYPE,
    DATE_RANGE_FILTER_TYPE,
    APPLICATION_RATE_FILTER_TYPE,
    DATE_FILTER_TYPE,
    ENTITIES_FILTER_TYPE,
    INNER_ENTITIES_FILTER_TYPE
} from './constants';

const selectSprayEvents = (state: AppState): SprayEventsState =>
    state.sprayEvents;
const selectSprayEventId = (
    _state: AppState,
    props: SprayEventsProps
): SprayEventId => props.sprayEventId;

const selectSprayEventsArray = createDeepEqualSelector(
    selectSprayEvents,
    (sprayEventsState) => Object.values(sprayEventsState)
);

const selectOrderedSprayEvents = createDeepEqualSelector(
    selectSprayEventsArray,
    (sprayEventsState) => orderBy(sprayEventsState, 'startDate')
);

const selectSprayEventsKeyedById = createDeepEqualSelector(
    selectOrderedSprayEvents,
    (sprayEventsState) => keyBy(sprayEventsState, 'id')
);

const makeSelectSprayEventsArray = (): typeof selectSprayEventsArray =>
    selectSprayEventsArray;

const selectSprayEvent = createDeepEqualSelector(
    [selectSprayEventsKeyedById, selectSprayEventId],
    (sprayEventsState, sprayEventIdProp): SprayEvent | undefined =>
        get(sprayEventsState, sprayEventIdProp)
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectSprayEventExists = () =>
    createDeepEqualSelector(
        selectSprayEvent,
        (sprayEventState): boolean => !!sprayEventState
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectSprayEventEntityId = () =>
    createDeepEqualSelector(selectSprayEvent, (sprayEventState) =>
        get(sprayEventState, 'entityId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectSprayEventStartDate = () =>
    createDeepEqualSelector(selectSprayEvent, (sprayEventState) =>
        get(sprayEventState, 'startDate')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectSprayEventApplicatorId = () =>
    createDeepEqualSelector(selectSprayEvent, (sprayEventState) =>
        get(sprayEventState, ['sprayEvent', 'operatorPersonId'])
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectSprayEventEntities = () =>
    createDeepEqualSelector(selectSprayEvent, (sprayEventState) => {
        return get(sprayEventState, ['sprayEvent', 'sprayEntities']);
    });

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectSprayEventEntityName = () =>
    createDeepEqualSelector(selectSprayEvent, (sprayEventState) =>
        get(sprayEventState, ['entityName'])
    );

const selectSprayEventsVarietyIdsArray = createDeepEqualSelector(
    makeSelectSprayEventEntities(),
    makeSelectHarvestSchemaEntityTypeId(),
    selectEntities,
    (sprayEventEntities, schemaEntityTypeId, entities) =>
        getSprayEventVarietiesArray(
            sprayEventEntities,
            schemaEntityTypeId,
            entities
        )
);

const selectDiarySearchQueryString = createDeepEqualSelector(
    [
        makeSelectQueryStringNumberValue('sprayProductId'),
        makeSelectQueryStringNumberValue('sprayEquipmentSetId'),
        makeSelectQueryStringNumberValue('applicationRate'),
        makeSelectQueryStringValue('startDate'),
        makeSelectQueryStringValue('endDate'),
        makeSelectQueryStringNumberValue('entities'),
        makeSelectQueryStringNumberValue('innerEntities')
    ],
    (
        sprayProductId,
        sprayEquipmentSetId,
        applicationRate,
        startDate,
        endDate,
        sprayEntityId,
        sprayInnerEntityId
    ) =>
        ({
            sprayProductId,
            sprayEquipmentSetId,
            applicationRate,
            startDate,
            endDate,
            sprayEntityId,
            sprayInnerEntityId
        }) as SprayEventSearchFilters
);

const selectDiaryFilterType = createDeepEqualSelector(
    [selectDiarySearchQueryString],
    (filters): SprayEventFilterType => {
        const {
            sprayProductId,
            sprayEquipmentSetId,
            applicationRate,
            startDate,
            endDate,
            sprayEntityId,
            sprayInnerEntityId
        } = filters;

        let filterType: SprayEventFilterType = DATE_FILTER_TYPE;

        if (sprayProductId) {
            filterType = PRODUCT_FILTER_TYPE;
        } else if (startDate && endDate) {
            filterType = DATE_RANGE_FILTER_TYPE;
        } else if (sprayEquipmentSetId) {
            filterType = EQUIPMENT_FILTER_TYPE;
        } else if (applicationRate) {
            filterType = APPLICATION_RATE_FILTER_TYPE;
        } else if (sprayEntityId) {
            filterType = ENTITIES_FILTER_TYPE;
        } else if (sprayInnerEntityId) {
            filterType = INNER_ENTITIES_FILTER_TYPE;
        }

        return filterType;
    }
);

const selectSprayEventsFilteredBySprayEventType = createDeepEqualSelector(
    [selectSprayEventsArray, selectSprayEventTypeIsWeed],
    (sprayEventsArray, isWeedSpray) =>
        sprayEventsArray.filter(
            ({ sprayEvent }) => sprayEvent.isWeedSpray === isWeedSpray
        )
);

const selectFilteredSprayEventsArray = createDeepEqualSelector(
    [
        selectSprayEventsFilteredBySprayEventType,
        selectDiaryFilterType,
        selectFirstDate,
        selectLastDate,
        selectDiarySearchQueryString
    ],
    (
        sprayEventsArray,
        filterType,
        firstDate: string,
        lastDate: string,
        searchFilters
    ) => {
        let filterFn;

        switch (filterType) {
            case PRODUCT_FILTER_TYPE: {
                const { sprayProductId } = searchFilters;
                filterFn = ({ sprayEvent: { sprayProducts } }: SprayEvent) => {
                    return sprayProducts.some(
                        ({ id }) => id === sprayProductId
                    );
                };
                break;
            }
            case DATE_RANGE_FILTER_TYPE: {
                const { startDate, endDate } = searchFilters as {
                    startDate: string;
                    endDate: string;
                };
                filterFn = (event: SprayEvent) =>
                    startDate <= event.startDate && event.startDate <= endDate;
                break;
            }
            case EQUIPMENT_FILTER_TYPE: {
                const { sprayEquipmentSetId } = searchFilters;
                filterFn = ({ sprayEvent }: SprayEvent) =>
                    sprayEquipmentSetId &&
                    sprayEvent.sprayEventOperations
                        .map((operator) => operator.sprayEquipmentSetId)
                        .includes(sprayEquipmentSetId);
                break;
            }
            case APPLICATION_RATE_FILTER_TYPE: {
                const { applicationRate } = searchFilters;
                filterFn = ({ sprayEvent }: SprayEvent) =>
                    sprayEvent.applicationRate === applicationRate;
                break;
            }
            case ENTITIES_FILTER_TYPE: {
                const { sprayEntityId } = searchFilters;
                filterFn = ({ entityId }: SprayEvent) => {
                    return entityId === sprayEntityId;
                };
                break;
            }
            case INNER_ENTITIES_FILTER_TYPE: {
                const { sprayInnerEntityId } = searchFilters;
                filterFn = ({ sprayEvent: { sprayEntities } }: SprayEvent) => {
                    return sprayEntities?.find(
                        (entity) => entity.id === sprayInnerEntityId
                    );
                };
                break;
            }
            default:
                filterFn = (event: SprayEvent) =>
                    firstDate <= event.startDate && event.startDate <= lastDate;
                break;
        }

        return sprayEventsArray.filter(filterFn);
    }
);

const selectFilteredSprayEventsUuids = createDeepEqualSelector(
    [selectFilteredSprayEventsArray],
    (sprayEvents): string[] => sprayEvents.map(({ uuid }) => uuid)
);

export {
    selectSprayEvents,
    selectSprayEventId,
    selectSprayEventsArray,
    selectSprayEvent,
    selectSprayEventsKeyedById,
    selectOrderedSprayEvents,
    selectSprayEventsVarietyIdsArray,
    makeSelectSprayEventExists,
    makeSelectSprayEventEntityId,
    makeSelectSprayEventStartDate,
    makeSelectSprayEventsArray,
    makeSelectSprayEventApplicatorId,
    makeSelectSprayEventEntities,
    makeSelectSprayEventEntityName,
    selectDiarySearchQueryString,
    selectDiaryFilterType,
    selectSprayEventsFilteredBySprayEventType,
    selectFilteredSprayEventsArray,
    selectFilteredSprayEventsUuids
};
